import { useState } from 'react'
import { Link } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { relativeTime } from '@/lib/format'
import { Button } from '@/ui/Button'
import { Modal } from '@/ui/Modal'
import { ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'
import { Verdict } from './verdict'

type Device = Schema<'DeviceOut'>

/** A browser that has checked in within this long is *live* — `ExtensionPanel`'s window. */
const LIVE_WINDOW_MS = 180_000

/**
 * Session capture — the card `outreach.html#health` draws third in its
 * right-hand column (parity P-HLTH-06, 2026-09-13): an intro sentence, three
 * rows (Extension · Session · Stored in), an expiry note, and a footer with
 * the way to pair and the way to revoke.
 *
 * **The rows say what the product does, not what the mockup's architecture
 * did.** The mockup describes an extension that "hands that session to us"
 * and a cookie "stored in Parameter Store"; that design went with ARCH1
 * (2026-08-01). Today the searches run inside the tenant's own paired browser
 * and nothing of the session leaves it (`app/extension/__init__.py`), so the
 * third row reads *Your browser only · Never sent* — the same shape, the true
 * sentence. The footer's *Pair a browser* leads to the pairing code on
 * Settings → Connections rather than duplicating that flow here.
 */
export function SessionCaptureCard(): JSX.Element {
  const toast = useToast()
  const [confirming, setConfirming] = useState<Device | null>(null)
  const now = new Date()

  const devices = useQuery(
    (signal) => call(() => api.GET('/api/v1/extension/devices', { signal })),
    [],
  )
  const revoke = useMutation((deviceId: string) =>
    call(() =>
      api.DELETE('/api/v1/extension/devices/{device_id}', {
        params: { path: { device_id: deviceId } },
      }),
    ),
  )

  const rows = (devices.state.data?.devices ?? []).filter((row) => row.revoked_at === null)
  // The most recently seen paired browser is the one the rows describe.
  const latest = [...rows].sort(
    (a, b) => new Date(b.last_seen_at ?? 0).getTime() - new Date(a.last_seen_at ?? 0).getTime(),
  )[0]
  const live =
    latest?.last_seen_at !== null &&
    latest?.last_seen_at !== undefined &&
    now.getTime() - new Date(latest.last_seen_at).getTime() < LIVE_WINDOW_MS

  return (
    <article className="ar-card">
      <div className="ar-card__header">
        <h3>Session capture</h3>
        <span className="ar-card__spacer" />
        {devices.state.status === 'ready' ? (
          latest ? (
            <Verdict tone={live ? 'success' : 'neutral'}>{live ? 'Active' : 'Paired'}</Verdict>
          ) : (
            <Verdict tone="neutral">Not paired</Verdict>
          )
        ) : null}
      </div>
      <div className="ar-card__body ar-stack ar-stack--tight">
        <p className="ar-muted" style={{ margin: 0, fontSize: 'var(--ar-text-body-sm-size)' }}>
          LinkedIn has no search API, so post search runs on your own signed-in session, in your own
          browser. The extension runs the searches there and sends back only the posts it finds —
          never your messages, your feed, or anyone&rsquo;s profile.
        </p>

        {devices.state.status === 'loading' ? (
          <Loading label="Checking your paired browsers" lines={3} />
        ) : null}
        {devices.state.status === 'error' ? (
          <ErrorState
            failure={devices.state.failure}
            title="Could not check your paired browsers"
            onRetry={devices.refetch}
          />
        ) : null}
        <InlineError failure={revoke.state.failure} />

        {devices.state.status === 'ready' ? (
          <table className="ar-table app-health__facts">
            <caption className="ar-visually-hidden">The browser that runs your searches</caption>
            <tbody>
              <tr>
                <td>Extension</td>
                <td className="ar-num">
                  {latest
                    ? `${latest.label}${latest.last_version ? ` · v${latest.last_version}` : ''}`
                    : '—'}
                </td>
                <td className="ar-num">
                  {latest ? (
                    <Verdict tone="success">Installed</Verdict>
                  ) : (
                    <Verdict tone="neutral">Not paired</Verdict>
                  )}
                </td>
              </tr>
              <tr>
                <td>Session</td>
                <td className="ar-num">
                  {latest?.last_seen_at ? `seen ${relativeTime(latest.last_seen_at, now)}` : '—'}
                </td>
                <td className="ar-num">
                  {live ? (
                    <Verdict tone="success">Live</Verdict>
                  ) : latest ? (
                    <Verdict tone="warning">Not running</Verdict>
                  ) : (
                    <Verdict tone="neutral">Not yet</Verdict>
                  )}
                </td>
              </tr>
              <tr>
                <td>Stored in</td>
                <td className="ar-num">Your browser only</td>
                <td className="ar-num">
                  <Verdict tone="neutral">Never sent</Verdict>
                </td>
              </tr>
            </tbody>
          </table>
        ) : null}

        <span className="ar-field__help">
          A LinkedIn session expires on its own every few weeks. We email you the day it happens —
          searching stops, nothing else does.
        </span>
      </div>
      <div className="ar-card__footer">
        <Link className="ar-btn ar-btn--ghost ar-btn--sm" to="/settings/connections#extension">
          Pair a browser
        </Link>
        <span className="ar-card__spacer" />
        {latest ? (
          <Button
            tone="quiet"
            size="sm"
            onClick={() => {
              setConfirming(latest)
            }}
          >
            Revoke
          </Button>
        ) : null}
      </div>

      {confirming !== null ? (
        <Modal
          title="Disconnect this browser?"
          onClose={() => {
            setConfirming(null)
          }}
          footer={
            <>
              <Button
                tone="ghost"
                onClick={() => {
                  setConfirming(null)
                }}
              >
                Keep it connected
              </Button>
              <Button
                tone="danger"
                busy={revoke.pending}
                busyLabel="Disconnecting"
                onClick={() => {
                  void (async () => {
                    const target = confirming
                    const result = await revoke.run(target.id)
                    setConfirming(null)
                    if (!result.ok) return
                    devices.refetch()
                    toast.show({ kind: 'success', title: 'Browser disconnected' })
                  })()
                }}
              >
                Disconnect
              </Button>
            </>
          }
        >
          <p style={{ marginTop: 0 }}>
            <strong>{confirming.label}</strong> will stop running LinkedIn searches for this
            organisation. To use it again you will need to pair it with a new code.
          </p>
        </Modal>
      ) : null}
    </article>
  )
}
